// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import { renameKeys, parseBool } from '~/helpers';
import { ACTIVE_ANNOUNCEMENTS_LIST } from '~/gql';
import { ApolloCache } from 'apollo-cache';

const sortWithPinnedAndBookmarked = R.sortWith([
  R.descend(R.prop('pinned')),
  R.descend(R.prop('bookmarked')),
]);

const getAnnouncementsList = ({ url, checkPropIfExist }) => async () => {
  const params = {
    SortBy: 'PublishedDateTimeUtc',
    PageSize: 1,
    PageNum: 1,
  };
  const { data } = await HttpService.client.get(url, { params });
  return R.pipe(
    R.propOr([], checkPropIfExist),
    R.sortWith([
      R.descend(R.prop('Pin')),
      R.descend(R.prop('PublishedDateTimeUtc')),
    ]),
    R.map(
      R.pipe(
        renameKeys({
          Id: 'id',
          Title: 'title',
          PublishedDateTimeUtc: 'date',
          Content: 'content',
          Pin: 'pinned',
          Bookmark: 'bookmarked',
          Type: 'type',
        }),
        R.evolve({
          pinned: parseBool,
          bookmarked: parseBool,
          type: R.cond([
            [R.equals('N'), R.always('news')],
            [R.equals('A'), R.always('announcements')],
          ]),
        }),
        R.assoc(
          '__typename',
          R.cond([
            [
              R.propEq('type', 'announcements'),
              R.always('ActiveAnnouncementListItem'),
            ],
            [R.propEq('type', 'news'), R.always('ActiveNewsListItem')],
          ]),
        ),
        R.assoc('read', false),
      ),
    ),
  )(data);
};

const getActiveAnnouncementsList = getAnnouncementsList({
  url: '/Information/GetMsNewAnnouncementList',
  checkPropIfExist: 'MsAnnouncementList',
});

export const resolvers = {
  Query: {
    activeAnnouncementsList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?(Announcement[])> => {
      let activeAnnouncementsList: Announcement[];
      try {
        activeAnnouncementsList = cache.readQuery({
          query: ACTIVE_ANNOUNCEMENTS_LIST,
        }).activeAnnouncementsList;
      } catch (e) {
        activeAnnouncementsList = R.tryCatch(
          async () =>
            sortWithPinnedAndBookmarked(await getActiveAnnouncementsList()),
          () => [],
        )();

        cache.writeData({
          data: { activeAnnouncementsList },
          variables: {},
        });
      }

      return activeAnnouncementsList;
    },
  },
  Mutation: {
    markAnnouncementsReadMutation: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      vars: MarkAnnouncementsReadInputMutation,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      const {
        input: { ids = [] },
      } = vars;
      const url = '/Information/AnnouncementIsRead';
      const {
        data: { ReturnCode, ReturnMessage = 'Done' },
      } = await HttpService.client.post(url, {
        id: ids,
      });

      // Why it is wrapped in tryCatch?
      // If new announcements were never loaded, an attempt to read ACTIVE_ANNOUNCEMENTS_LIST query throws an error
      const { activeAnnouncementsList } = R.tryCatch(
        () =>
          cache.readQuery({
            query: ACTIVE_ANNOUNCEMENTS_LIST,
          }),
        () => ({ activeAnnouncementsList: [] }),
      )();
      const updatedArticles = (activeAnnouncementsList || []).filter(
        a => !ids.includes(a.id),
      );

      // Update cache
      cache.writeQuery({
        query: ACTIVE_ANNOUNCEMENTS_LIST,
        data: { activeAnnouncementsList: updatedArticles },
        variables: {},
      });

      return {
        code: ReturnCode,
        message: ReturnMessage,
        __typename: 'MessagePayload',
      };
    },
  },
};
